import React, { createContext, useContext, useEffect, useState } from "react";
import { useEnabledModules } from "@/hooks/useEnabledModules";
import { useModuleSession } from "@/contexts/ModuleSessionContext";

// Define available vertical themes
type VerticalTheme = "default" | "edilizia";

// Map module keys to their themes
const MODULE_THEMES: Record<string, VerticalTheme> = {
  edilizia: "edilizia",
  vertical_edilizia: "edilizia", // alias
};

// Priority order for theme selection (first match wins)
const THEME_PRIORITY: string[] = ["edilizia", "vertical_edilizia"];

interface ThemeContextType {
  activeTheme: VerticalTheme;
  isLoading: boolean;
}

const ThemeContext = createContext<ThemeContextType>({
  activeTheme: "default",
  isLoading: true,
});

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const { isModuleEnabled, isLoading: modulesLoading } = useEnabledModules();
  const { currentModule } = useModuleSession();
  // Tema sincrono dalla route: evita flash blu→verde in attesa di useEnabledModules.
  // Su /construction/* Edilizia è certa (ConstructionProtectedRoute).
  const [activeTheme, setActiveTheme] = useState<VerticalTheme>(() =>
    currentModule === "construction" ? "edilizia" : "default",
  );

  useEffect(() => {
    // Su Core non applicare tema verticali (evita leak da sessione Edilizia in localStorage)
    if (currentModule === "core") {
      setActiveTheme("default");
      return;
    }

    // Route Construction: tema Edilizia subito (anche in login, prima del tenant)
    if (currentModule === "construction") {
      setActiveTheme("edilizia");
      return;
    }

    if (modulesLoading) return;

    // Check enabled modules in priority order
    let detectedTheme: VerticalTheme = "default";
    
    for (const moduleKey of THEME_PRIORITY) {
      if (isModuleEnabled(moduleKey)) {
        detectedTheme = MODULE_THEMES[moduleKey] || "default";
        break;
      }
    }

    setActiveTheme(detectedTheme);
  }, [isModuleEnabled, modulesLoading, currentModule]);

  // Apply theme class to document root
  useEffect(() => {
    const root = document.documentElement;
    
    // Remove all theme classes
    root.classList.remove("theme-default", "theme-edilizia");
    
    // Add active theme class
    root.classList.add(`theme-${activeTheme}`);
    
    return () => {
      root.classList.remove(`theme-${activeTheme}`);
    };
  }, [activeTheme]);

  return (
    <ThemeContext.Provider value={{ activeTheme, isLoading: modulesLoading }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}
